import { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { getAllChecklistInstances, deleteChecklistInstance } from './checklistApi';
import { Skeleton } from '../../components/Loaders/Skeleton/Skeleton';
import DeleteConfirmationDialog from '../../components/dialogs/DeleteConfirmationDialog';
import { Trash2 } from 'lucide-react';
import './ChecklistInstancesList.scss';

function ChecklistInstancesList({ refreshTrigger }) {
    const [instances, setInstances] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [instanceToDelete, setInstanceToDelete] = useState(null);
    const [deleteError, setDeleteError] = useState(null);

    const fetchInstances = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const instancesData = await getAllChecklistInstances();
            setInstances(instancesData);
        } catch (err) {
            setError('אירעה שגיאה בטעינת הרשימות');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchInstances();
    }, [fetchInstances, refreshTrigger]);

    const handleDeleteClick = (e, instance) => {
        e.preventDefault();
        e.stopPropagation();
        setInstanceToDelete(instance);
        setDeleteError(null);
        setDeleteConfirmOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!instanceToDelete) return;

        try {
            await deleteChecklistInstance(instanceToDelete._id);
            setDeleteConfirmOpen(false);
            setInstanceToDelete(null);
            fetchInstances(); // Refresh the list
        } catch (error) {
            console.error('Failed to delete checklist instance', error);
            setDeleteError('אירעה שגיאה במחיקת המופע. אנא נסה שנית.');
        }
    };

    const handleDeleteCancel = () => {
        setDeleteConfirmOpen(false);
        setInstanceToDelete(null);
        setDeleteError(null);
    };

    if (loading) {
        return (
            <div className="checklist-instances-section">
                <h3 className="section-title">רשומות אחרונות</h3>
                <div className="checklist-instances-list">
                    <Skeleton>
                        <Skeleton.Block height="100px" />
                        <Skeleton.Block height="100px" />
                        <Skeleton.Block height="100px" />
                    </Skeleton>
                </div>
            </div>
        );
    }
    if (!instances || instances.length == 0) {
        return null;
    }

    if (error) {
        return (
            <div className="checklist-instances-section">
                <h3 className="section-title">רשומות אחרונות</h3>
                <div className="checklist-instances-error">
                    <p>{error}</p>
                    <button onClick={fetchInstances} className="retry-button">
                        נסה שנית
                    </button>
                </div>
            </div>
        );
    }

    if (instances.length === 0) {
        return null;
    }

    return (
        <div className="checklist-instances-section">
            <h3 className="section-title">רשומות אחרונות</h3>
            <div className="checklist-instances-list">
                {instances.map(instance => {
                    const creatorName = instance.createdBy
                        ? `${instance.createdBy.name || ''}`.trim() || 'לא ידוע'
                        : 'לא ידוע';
                    const createdDate = new Date(instance.createdAt).toLocaleString('he-IL', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                    });

                    return (
                        <Link
                            key={instance._id}
                            to={`/checklists/instance/${instance._id}`}
                            className="instance-card"
                        >
                            <div className="instance-content">
                                <h4 className="instance-name">{instance.name}</h4>
                                <div className="instance-meta">
                                    <span className="creator">נוצר על ידי: {creatorName}</span>
                                    <span className="date">{createdDate}</span>
                                </div>
                            </div>
                            <div className="instance-actions">
                                <button
                                    className="action-btn delete-btn"
                                    onClick={(e) => handleDeleteClick(e, instance)}
                                    title="מחק מופע"
                                >
                                    <Trash2 size={18} />
                                </button>
                            </div>
                        </Link>
                    );
                })}
            </div>

            <DeleteConfirmationDialog
                open={deleteConfirmOpen}
                onClose={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                title={instanceToDelete ? `האם אתה בטוח שברצונך למחוק את הצ'קליסט שנוצר ע"י ${instanceToDelete.createdBy?.name || 'לא ידוע'} ב${new Date(instanceToDelete.createdAt).toLocaleString('he-IL', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                })}?` : ''}
                showError={!!deleteError}
                errorMessage={deleteError}
                onCloseError={() => setDeleteError(null)}
            />
        </div>
    );
}

ChecklistInstancesList.propTypes = {
    refreshTrigger: PropTypes.number,
    onInstancesLoaded: PropTypes.func,
};

export default ChecklistInstancesList;